// Settings Module
import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { t } from '../core/i18n.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getCurrentUser } from '../utils/helpers.js';

/**
 * Display the settings page
 */
export async function showSettings() {
    // Update navigation and page title
    updateActiveNavigation('settings');
    updatePageTitle('Settings');
    
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view settings');
        return;
    }

    // Feature gating: hide settings for features this account has turned off.
    // SuperAdmin bypasses (isFeatureDisabled returns false) and sees everything.
    const ttDisabled = !!(window.permissions && window.permissions.isFeatureDisabled('timetracking'));
    const smartTimeDisabled = !!(window.permissions && window.permissions.isFeatureDisabled('smarttime'));
    const timeBudgetDisabled = !!(window.permissions && window.permissions.isFeatureDisabled('time_budget'));

    mainContent.innerHTML = `
        <div class="px-4 sm:px-6 lg:px-8">
            <!-- Loading spinner -->
            <div id="settings-loading" class="flex justify-center items-center py-12">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>

            <!-- Settings Content -->
            <div id="settings-content" class="hidden space-y-6">
                
                <!-- Time Tracking Settings Group -->
                ${!ttDisabled ? `
                <div class="bg-white shadow rounded-lg border-2 border-gray-200">
                    <!-- Group Header -->
                    <div class="px-6 py-4 bg-gray-50 border-b-2 border-gray-200">
                        <h2 class="text-xl font-semibold text-gray-900">${t('settings.timeTracking')}</h2>
                        <p class="mt-1 text-sm text-gray-600">${t('settings.timeTrackingDescription')}</p>
                    </div>
                    
                    <!-- Group Content -->
                    <div class="px-6 py-5 space-y-6">
                        <!-- Enable/Disable External Contact Access -->
                        <div class="flex items-start">
                            <div class="flex items-center h-5">
                                <input id="external-logs-enabled" type="checkbox" 
                                       onchange="handleExternalLogsEnabledChange()"
                                       class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded">
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="external-logs-enabled" class="font-medium text-gray-700">
                                    ${t('settings.allowExternal')}
                                </label>
                                <p class="text-gray-500">${t('settings.allowExternalDescription')}</p>
                            </div>
                        </div>

                        <!-- Access Restriction -->
                        <div id="date-restriction-section" class="ml-7 space-y-3 hidden">
                            <p class="text-sm font-medium text-gray-700">${t('settings.accessRestrictionType')}</p>

                            <!-- No restriction -->
                            <div class="flex items-center">
                                <input id="restriction-type-none" type="radio" name="restriction-type" value="none"
                                       onchange="handleRestrictionTypeChange()"
                                       class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                                       checked>
                                <label for="restriction-type-none" class="ml-3 text-sm text-gray-700">
                                    ${t('settings.noRestriction')}
                                </label>
                            </div>

                            <!-- Restrict to specific date -->
                            <div class="flex items-center">
                                <input id="restriction-type-date" type="radio" name="restriction-type" value="date"
                                       onchange="handleRestrictionTypeChange()"
                                       class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300">
                                <label for="restriction-type-date" class="ml-3 text-sm text-gray-700">
                                    ${t('settings.restrictDate')}
                                </label>
                            </div>

                            <div id="date-picker-section" class="ml-7 hidden relative">
                                <label for="restriction-date-button" class="block text-sm font-medium text-gray-700 mb-1">
                                    ${t('settings.showLogsUpTo')}
                                </label>
                                <button type="button" 
                                        id="restriction-date-button"
                                        onclick="window.calendarInstances?.['restriction-date-button']?.toggle()"
                                        class="flex items-center justify-between max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm hover:bg-gray-50 text-sm">
                                    <span id="restriction-date-display" class="text-gray-700">Select date</span>
                                    <svg class="ml-2 w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"/>
                                    </svg>
                                </button>
                                <input type="hidden" id="restriction-date">
                                <div id="restriction-calendar-popup" class="hidden fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-4" style="width: 320px;"></div>
                            </div>

                            <!-- Delay by days -->
                            <div class="flex items-center">
                                <input id="restriction-type-delay" type="radio" name="restriction-type" value="delay"
                                       onchange="handleRestrictionTypeChange()"
                                       class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300">
                                <label for="restriction-type-delay" class="ml-3 text-sm text-gray-700">
                                    ${t('settings.delayDays')}
                                </label>
                            </div>

                            <div id="delay-days-section" class="ml-7 hidden">
                                <label for="restriction-delay-days" class="block text-sm font-medium text-gray-700 mb-1">
                                    ${t('settings.delayDaysLabel')}
                                </label>
                                <input type="number" id="restriction-delay-days" min="1" step="1"
                                       class="block w-24 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="e.g. 3">
                                <p class="mt-1 text-xs text-gray-500">${t('settings.delayDaysDescription')}</p>
                            </div>

                            <!-- Lower-bound (start date) restriction -->
                            <div class="border-t border-gray-200 pt-3">
                                <div class="flex items-start">
                                    <div class="flex items-center h-5">
                                        <input id="start-date-restriction-enabled" type="checkbox"
                                               onchange="handleStartDateRestrictionEnabledChange()"
                                               class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded">
                                    </div>
                                    <div class="ml-3 text-sm">
                                        <label for="start-date-restriction-enabled" class="font-medium text-gray-700">
                                            ${t('settings.startDateRestriction')}
                                        </label>
                                        <p class="text-gray-500">${t('settings.startDateRestrictionDescription')}</p>
                                    </div>
                                </div>

                                <div id="start-date-picker-section" class="ml-7 mt-2 hidden relative">
                                    <label for="start-date-restriction-button" class="block text-sm font-medium text-gray-700 mb-1">
                                        ${t('settings.startDateLabel')}
                                    </label>
                                    <button type="button"
                                            id="start-date-restriction-button"
                                            onclick="window.calendarInstances?.['start-date-restriction-button']?.toggle()"
                                            class="flex items-center justify-between max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm hover:bg-gray-50 text-sm">
                                        <span id="start-date-restriction-display" class="text-gray-700">Select date</span>
                                        <svg class="ml-2 w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"/>
                                        </svg>
                                    </button>
                                    <input type="hidden" id="start-date-restriction">
                                    <div id="start-date-restriction-calendar-popup" class="hidden fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-4" style="width: 320px;"></div>
                                </div>
                            </div>
                        </div>

                        <!-- SmartTime Settings -->
                        ${!smartTimeDisabled ? `
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('settings.smartTimeTitle')}</label>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label for="smarttime-min-deviation" class="block text-sm text-gray-600 mb-1">${t('settings.smartTimeMinDeviation')}</label>
                                    <input type="number" id="smarttime-min-deviation" min="0" step="1"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                           placeholder="e.g., 10">
                                </div>
                                <div>
                                    <label for="smarttime-random-deviation" class="block text-sm text-gray-600 mb-1">${t('settings.smartTimeRandomDeviation')}</label>
                                    <input type="number" id="smarttime-random-deviation" min="0" step="1"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                           placeholder="e.g., 15">
                                </div>
                            </div>
                            <p class="mt-2 text-xs text-gray-500">${t('settings.smartTimeDescription')}</p>
                        </div>
                        ` : ''}

                        <!-- Time Budget Settings -->
                        ${!timeBudgetDisabled ? `
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('settings.timeBudgetTitle')}</label>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label for="time-budget-red-limit-below" class="block text-sm text-gray-600 mb-1">${t('settings.timeBudgetRedLimitBelow')}</label>
                                    <input type="number" id="time-budget-red-limit-below" min="0" step="1"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                           placeholder="${t('settings.timeBudgetRedLimitPlaceholder')}">
                                </div>
                                <div>
                                    <label for="time-budget-red-limit-above" class="block text-sm text-gray-600 mb-1">${t('settings.timeBudgetRedLimitAbove')}</label>
                                    <input type="number" id="time-budget-red-limit-above" min="0" step="1"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                           placeholder="${t('settings.timeBudgetRedLimitPlaceholder')}">
                                </div>
                            </div>
                            <p class="mt-2 text-xs text-gray-500">${t('settings.timeBudgetDescription')}</p>
                        </div>
                        ` : ''}

                        <!-- Save Button -->
                        <div class="flex justify-end pt-4 border-t border-gray-200">
                            <button type="button" onclick="saveSettings()" 
                                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                <svg class="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                                ${t('settings.saveSettings')}
                            </button>
                        </div>
                    </div>
                </div>
                ` : ''}

                <!-- Report Template Settings Group -->
                <div class="bg-white shadow rounded-lg border-2 border-gray-200">
                    <!-- Group Header -->
                    <div class="px-6 py-4 bg-gray-50 border-b-2 border-gray-200">
                        <h2 class="text-xl font-semibold text-gray-900">${t('settings.reportTemplates')}</h2>
                        <p class="mt-1 text-sm text-gray-600">${t('settings.reportTemplatesDescription')}</p>
                    </div>
                    
                    <!-- Group Content -->
                    <div class="px-6 py-5 space-y-6">
                        <!-- Default Time Tracking Report Template -->
                        ${!ttDisabled ? `
                        <div class="space-y-2">
                            <label for="time-tracking-template-select" class="block text-sm font-medium text-gray-700">
                                ${t('settings.timeTrackingReportTemplate')}
                            </label>
                            <p class="text-sm text-gray-500">${t('settings.timeTrackingReportTemplateDescription')}</p>
                            <select id="time-tracking-template-select" 
                                    class="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="">${t('settings.noneAskEveryTime')}</option>
                            </select>
                        </div>
                        ` : ''}

                        <!-- Default Environmental (temp/hum) Report Template -->
                        <div class="space-y-2">
                            <label for="environmental-template-select" class="block text-sm font-medium text-gray-700">
                                ${t('settings.environmentalReportTemplate')}
                            </label>
                            <p class="text-sm text-gray-500">${t('settings.environmentalReportTemplateDescription')}</p>
                            <select id="environmental-template-select" 
                                    class="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="">${t('settings.noneAskEveryTime')}</option>
                            </select>
                        </div>

                        <!-- Save Button -->
                        <div class="flex justify-end pt-4 border-t border-gray-200">
                            <button type="button" onclick="saveSettings()" 
                                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                <svg class="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                                ${t('settings.saveSettings')}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Future Setting Groups can be added here -->
                <!-- Example: -->
                <!--
                <div class="bg-white shadow rounded-lg border-2 border-gray-200">
                    <div class="px-6 py-4 bg-gray-50 border-b-2 border-gray-200">
                        <h2 class="text-xl font-semibold text-gray-900">Notification Settings</h2>
                        <p class="mt-1 text-sm text-gray-600">Configure notification preferences</p>
                    </div>
                    <div class="px-6 py-5 space-y-6">
                        <!-- Settings content here -->
                    </div>
                </div>
                -->

                <!-- Last Updated Info -->
                <div id="last-updated-section" class="hidden text-sm text-gray-500 text-right">
                    ${t('settings.lastUpdated')}: <span id="last-updated-time"></span> ${t('settings.by')} <span id="last-updated-user"></span>
                </div>

            </div>
        </div>
    `;

    // Load current settings
    await loadSettings();
}

/**
 * Load current settings from API
 */
async function loadSettings() {
    const loadingElement = document.getElementById('settings-loading');
    const contentElement = document.getElementById('settings-content');
    
    try {
        // Load settings and get current user
        const currentUser = getCurrentUser();
        
        const settings = await apiCall('/settings', {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });

        // Load report templates for the account
        const templates = await apiCall(`/report-templates/templates?account_id=${currentUser.account_id}`, {
            headers: auth.getAuthHeaders()
        });
        
        console.log('All templates:', templates);
        console.log('Templates with report_type:', templates.map(t => ({ name: t.name, report_type: t.report_type, is_released: t.is_released })));
        
        // Filter for Filter-type templates that are released
        const filterTemplates = templates.filter(t => t.report_type === 'filter' && t.is_released);
        
        console.log('Filter templates (released):', filterTemplates);
        
        // Populate template selector
        const templateSelect = document.getElementById('time-tracking-template-select');
        if (templateSelect) {
            // Keep the "None" option and add all filter templates
            filterTemplates.forEach(template => {
                const option = document.createElement('option');
                option.value = template._id;
                option.textContent = template.name;
                templateSelect.appendChild(option);
            });
            
            console.log('Added', filterTemplates.length, 'templates to selector');
        }

        // Populate environmental (temp/hum) template selector with the same filter templates
        const envTemplateSelect = document.getElementById('environmental-template-select');
        if (envTemplateSelect) {
            filterTemplates.forEach(template => {
                const option = document.createElement('option');
                option.value = template._id;
                option.textContent = template.name;
                envTemplateSelect.appendChild(option);
            });
        }
        
        // Load account to get default template setting
        const account = await apiCall(`/accounts/${currentUser.account_id}`, {
            headers: auth.getAuthHeaders()
        });
        
        // Set template from settings (preferred) or fallback to account for backward compatibility
        if (templateSelect) {
            if (settings.time_tracking_report_template_id) {
                templateSelect.value = settings.time_tracking_report_template_id;
            } else if (account.time_tracking_report_template_id) {
                // Backward compatibility: if still in account, use it
                templateSelect.value = account.time_tracking_report_template_id;
            }
        }

        if (envTemplateSelect && settings.environmental_report_template_id) {
            envTemplateSelect.value = settings.environmental_report_template_id;
        }
        
        // Populate form with current settings
        const enabledCheckbox = document.getElementById('external-logs-enabled');
        const smartTimeMinDeviation = document.getElementById('smarttime-min-deviation');
        const smartTimeRandomDeviation = document.getElementById('smarttime-random-deviation');
        
        if (enabledCheckbox) {
            enabledCheckbox.checked = settings.external_contact_time_logs_enabled || false;
        }
        
        // Set restriction type radio based on stored settings
        const hasDateRestriction = !!settings.external_contact_time_logs_date_restriction;
        const hasDelayDays = !!settings.external_contact_time_logs_delay_days;
        
        if (hasDateRestriction) {
            const radioDate = document.getElementById('restriction-type-date');
            if (radioDate) radioDate.checked = true;
            const restrictionDate = document.getElementById('restriction-date');
            if (restrictionDate) restrictionDate.value = settings.external_contact_time_logs_date_restriction;
            const datePickerSection = document.getElementById('date-picker-section');
            if (datePickerSection) datePickerSection.classList.remove('hidden');
        } else if (hasDelayDays) {
            const radioDelay = document.getElementById('restriction-type-delay');
            if (radioDelay) radioDelay.checked = true;
            const delayInput = document.getElementById('restriction-delay-days');
            if (delayInput) delayInput.value = settings.external_contact_time_logs_delay_days;
            const delaySection = document.getElementById('delay-days-section');
            if (delaySection) delaySection.classList.remove('hidden');
        } else {
            const radioNone = document.getElementById('restriction-type-none');
            if (radioNone) radioNone.checked = true;
        }

        // Lower-bound start-date restriction (independent of upper-bound choice)
        const startDateRestriction = settings.external_contact_time_logs_start_date_restriction;
        const startDateCheckbox = document.getElementById('start-date-restriction-enabled');
        const startDateInput = document.getElementById('start-date-restriction');
        if (startDateCheckbox) {
            startDateCheckbox.checked = !!startDateRestriction;
        }
        if (startDateInput && startDateRestriction) {
            startDateInput.value = startDateRestriction;
        }
        const startDatePickerSection = document.getElementById('start-date-picker-section');
        if (startDatePickerSection) {
            startDatePickerSection.classList.toggle('hidden', !startDateRestriction);
        }

        if (smartTimeMinDeviation) {
            smartTimeMinDeviation.value = settings.smart_time_minimum_deviation_minutes ?? 0;
        }
        if (smartTimeRandomDeviation) {
            smartTimeRandomDeviation.value = settings.smart_time_random_deviation_minutes ?? 0;
        }

        const timeBudgetRedLimitBelow = document.getElementById('time-budget-red-limit-below');
        const timeBudgetRedLimitAbove = document.getElementById('time-budget-red-limit-above');
        if (timeBudgetRedLimitBelow) {
            timeBudgetRedLimitBelow.value =
                settings.time_budget_red_limit_below ?? '';
        }
        if (timeBudgetRedLimitAbove) {
            timeBudgetRedLimitAbove.value =
                settings.time_budget_red_limit_above ?? '';
        }
        
        // Update visibility of date restriction section
        handleExternalLogsEnabledChange();
        
        // Show last updated info if available
        if (settings.updated_at && settings.updated_by) {
            const lastUpdatedSection = document.getElementById('last-updated-section');
            const lastUpdatedTime = document.getElementById('last-updated-time');
            const lastUpdatedUser = document.getElementById('last-updated-user');
            
            if (lastUpdatedSection && lastUpdatedTime && lastUpdatedUser) {
                lastUpdatedTime.textContent = new Date(settings.updated_at).toLocaleString('en-GB');
                lastUpdatedUser.textContent = settings.updated_by;
                lastUpdatedSection.classList.remove('hidden');
            }
        }
        
    } catch (error) {
        console.error('Error loading settings:', error);
        showError('Failed to load settings');
    } finally {
        // Always hide loading and show content
        if (loadingElement) {
            loadingElement.className = '';  // Remove all classes
            loadingElement.style.display = 'none';
        }
        if (contentElement) {
            contentElement.classList.remove('hidden');
            contentElement.style.display = 'block';
        }
        
        // Initialize single date picker for restriction date
        setTimeout(() => {
            // Always re-initialize on each settings load to pick up fresh DOM
            if (window.calendarInstances) delete window.calendarInstances['restriction-date-button'];
            window.restrictionCalendar = null;

            if (window.createRangePicker) {
                window.restrictionCalendar = window.createRangePicker({
                    buttonId: 'restriction-date-button',
                    displayId: 'restriction-date-display',
                    popupId: 'restriction-calendar-popup',
                    fromInputId: 'restriction-date',
                    toInputId: 'restriction-date',
                    singleDate: true,
                    buttonText: 'Select date',
                    onApply: () => {}
                });
                // Populate display from the hidden input value set during loadSettings
                const restrictionDateInput = document.getElementById('restriction-date');
                const inst = window.calendarInstances?.['restriction-date-button'];
                if (inst && restrictionDateInput?.value) {
                    inst.state.startDate = new Date(restrictionDateInput.value + 'T00:00:00');
                    inst.updateDisplay();
                }
            }

            // Initialize start-date (lower-bound) picker
            if (window.calendarInstances) delete window.calendarInstances['start-date-restriction-button'];
            if (window.createRangePicker) {
                window.createRangePicker({
                    buttonId: 'start-date-restriction-button',
                    displayId: 'start-date-restriction-display',
                    popupId: 'start-date-restriction-calendar-popup',
                    fromInputId: 'start-date-restriction',
                    toInputId: 'start-date-restriction',
                    singleDate: true,
                    buttonText: 'Select date',
                    onApply: () => {}
                });
                const startDateInput2 = document.getElementById('start-date-restriction');
                const startInst = window.calendarInstances?.['start-date-restriction-button'];
                if (startInst && startDateInput2?.value) {
                    startInst.state.startDate = new Date(startDateInput2.value + 'T00:00:00');
                    startInst.updateDisplay();
                }
            }
        }, 100);
    }
}

/**
 * Handle changes to the external logs enabled checkbox
 */
export function handleExternalLogsEnabledChange() {
    const enabledCheckbox = document.getElementById('external-logs-enabled');
    const dateRestrictionSection = document.getElementById('date-restriction-section');
    
    if (enabledCheckbox && dateRestrictionSection) {
        if (enabledCheckbox.checked) {
            dateRestrictionSection.classList.remove('hidden');
        } else {
            dateRestrictionSection.classList.add('hidden');
        }
    }
}

/**
 * Handle changes to the restriction type radio buttons
 */
export function handleRestrictionTypeChange() {
    const selectedType = document.querySelector('input[name="restriction-type"]:checked')?.value;
    const datePickerSection = document.getElementById('date-picker-section');
    const delayDaysSection = document.getElementById('delay-days-section');
    if (datePickerSection) datePickerSection.classList.toggle('hidden', selectedType !== 'date');
    if (delayDaysSection) delayDaysSection.classList.toggle('hidden', selectedType !== 'delay');
}

/**
 * Handle changes to the start-date (lower-bound) restriction checkbox
 */
export function handleStartDateRestrictionEnabledChange() {
    const checkbox = document.getElementById('start-date-restriction-enabled');
    const section = document.getElementById('start-date-picker-section');
    if (checkbox && section) {
        section.classList.toggle('hidden', !checkbox.checked);
    }
}

/**
 * Save settings to API
 */
export async function saveSettings() {
    try {
        const currentUser = getCurrentUser();
        const enabledCheckbox = document.getElementById('external-logs-enabled');
        const restrictionDate = document.getElementById('restriction-date');
        const templateSelect = document.getElementById('time-tracking-template-select');
        const smartTimeMinDeviation = document.getElementById('smarttime-min-deviation');
        const smartTimeRandomDeviation = document.getElementById('smarttime-random-deviation');
        
        // Save settings
        const settingsUpdate = {
            external_contact_time_logs_enabled: enabledCheckbox.checked
        };
        
        // Only include date restriction if checkbox is checked and date is provided
        const restrictionType = document.querySelector('input[name="restriction-type"]:checked')?.value || 'none';
        const delayDaysInput = document.getElementById('restriction-delay-days');
        if (restrictionType === 'date' && restrictionDate?.value) {
            settingsUpdate.external_contact_time_logs_date_restriction = restrictionDate.value;
            settingsUpdate.external_contact_time_logs_delay_days = null;
        } else if (restrictionType === 'delay' && delayDaysInput?.value) {
            settingsUpdate.external_contact_time_logs_delay_days = parseInt(delayDaysInput.value, 10);
            settingsUpdate.external_contact_time_logs_date_restriction = null;
        } else {
            settingsUpdate.external_contact_time_logs_date_restriction = null;
            settingsUpdate.external_contact_time_logs_delay_days = null;
        }

        // Independent lower-bound (start date) restriction
        const startDateCheckbox = document.getElementById('start-date-restriction-enabled');
        const startDateInput = document.getElementById('start-date-restriction');
        if (startDateCheckbox?.checked && startDateInput?.value) {
            settingsUpdate.external_contact_time_logs_start_date_restriction = startDateInput.value;
        } else {
            settingsUpdate.external_contact_time_logs_start_date_restriction = null;
        }

        if (smartTimeMinDeviation) {
            const minValue = smartTimeMinDeviation.value.trim();
            settingsUpdate.smart_time_minimum_deviation_minutes = minValue ? parseInt(minValue, 10) : 0;
        }
        if (smartTimeRandomDeviation) {
            const randomValue = smartTimeRandomDeviation.value.trim();
            settingsUpdate.smart_time_random_deviation_minutes = randomValue ? parseInt(randomValue, 10) : 0;
        }

        const timeBudgetRedLimitBelow = document.getElementById('time-budget-red-limit-below');
        const timeBudgetRedLimitAbove = document.getElementById('time-budget-red-limit-above');
        if (timeBudgetRedLimitBelow) {
            const belowValue = timeBudgetRedLimitBelow.value.trim();
            settingsUpdate.time_budget_red_limit_below = belowValue ? Math.max(0, parseInt(belowValue, 10)) : null;
        }
        if (timeBudgetRedLimitAbove) {
            const aboveValue = timeBudgetRedLimitAbove.value.trim();
            settingsUpdate.time_budget_red_limit_above = aboveValue ? Math.max(0, parseInt(aboveValue, 10)) : null;
        }

        // Add report template selection to settings
        if (templateSelect) {
            settingsUpdate.time_tracking_report_template_id = templateSelect.value || null;
        }

        const envTemplateSelect = document.getElementById('environmental-template-select');
        if (envTemplateSelect) {
            settingsUpdate.environmental_report_template_id = envTemplateSelect.value || null;
        }
        
        await apiCall('/settings', {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(settingsUpdate)
        });
        
        showSuccess('Settings saved successfully');
        
        // Reload to show updated timestamp
        await loadSettings();
        
    } catch (error) {
        console.error('Error saving settings:', error);
        showError('Failed to save settings');
    }
}

// Export helper functions to window for inline onclick handlers
window.handleExternalLogsEnabledChange = handleExternalLogsEnabledChange;
window.handleRestrictionTypeChange = handleRestrictionTypeChange;
window.handleStartDateRestrictionEnabledChange = handleStartDateRestrictionEnabledChange;
window.saveSettings = saveSettings;
